import { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useTheme } from '../../context/ThemeContext';
import { FiSearch, FiFilter, FiX } from 'react-icons/fi';
import ConfirmModal from '../common/ConfirmModal';

// 🔥 COMPONENTE DO BADGE PARA USUÁRIOS NOVOS
const NewUserBadge = ({ userId }) => {
  const [isNewUser, setIsNewUser] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const { isDarkMode } = useTheme();

  useEffect(() => {
    const checkUserStatus = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', userId));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userIsNew = userData.isNewUser || false;
          const status = userData.userStatus || 'pending';

          setIsNewUser(userIsNew);
          setUserStatus(status);
        }
      } catch (error) {
        console.error('Erro ao verificar usuário:', error);
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, [userId]);

  // Só mostra o badge se for usuário novo E status for pending
  if (loading || !isNewUser || userStatus !== 'pending') {
    return null;
  }

  return (
    <div className="relative group">
      {/* Badge animado */}
      <div className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs font-bold rounded-full shadow-lg animate-pulse">
        <div className="w-2 h-2 bg-white rounded-full animate-ping" />
        <span>NOVO</span>
      </div>

      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
        <div className={`${isDarkMode ? 'bg-gray-700 text-white' : 'bg-black text-white'} text-xs py-1 px-2 rounded whitespace-nowrap`}>
          Novo usuário - Status pendente
        </div>
        <div className={`absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent ${isDarkMode ? 'border-t-gray-700' : 'border-t-black'
          }`}></div>
      </div>
    </div>
  );
};

// Componente de Paginação (atualizado para dark mode)
const Pagination = ({
  filteredItems,
  currentPage,
  indexOfFirstItem,
  indexOfLastItem,
  totalPages,
  paginate
}) => {
  const { isDarkMode } = useTheme();

  const buttonClass = `px-4 py-2 text-[10px] font-bold uppercase tracking-widest transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed ${isDarkMode
      ? 'border border-gray-600 text-white hover:bg-white hover:text-black hover:border-white'
      : 'border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white'
    } shadow-none hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.2)] dark:hover:shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)]`;



  return (
    <div className="flex items-center justify-between">
      <div className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        Mostrando <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>{indexOfFirstItem + 1}</span> a{' '}
        <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>{Math.min(indexOfLastItem, filteredItems.length)}</span> de{' '}
        <span className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>{filteredItems.length}</span> resultados
      </div>
      <div className="flex space-x-2">
        <button
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={buttonClass}
        >
          Anterior
        </button>
        <button
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={buttonClass}
        >
          Próxima
        </button>
      </div>
    </div>
  );
};

// Modal lateral para gerenciamento do app (atualizado para dark mode)
const AppManagementModal = ({
  isOpen,
  onClose,
  app,
  user,
  onDelete,
  onAlert,
  onUpdateMetrics
}) => {
  const [metrics, setMetrics] = useState({
    activeUsers: 0,
    totalDownloads: 0,
    retentionRate: 0,
    avgSessionTime: 0,
    conversionRate: 0
  });
  const [loading, setLoading] = useState(false);
  const [analysisReport, setAnalysisReport] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { isDarkMode } = useTheme();

  const handleAutoAnalysis = () => {
    const { activeUsers, totalDownloads } = metrics;
    
    if (!totalDownloads || totalDownloads === 0) {
      alert("Por favor, insira um valor válido para Downloads Totais.");
      return;
    }

    const rate = (activeUsers / totalDownloads) * 100;
    const rateFormatted = rate.toFixed(2);

    // Update metrics state
    setMetrics(prev => ({
      ...prev,
      conversionRate: parseFloat(rateFormatted),
      retentionRate: parseFloat(rateFormatted)
    }));

    // Generate text report
    let interpretation = "";
    if (rate > 20) interpretation = "Excelente engajamento. O app está retendo uma grande parte dos usuários.";
    else if (rate > 10) interpretation = "Bom engajamento. Taxas dentro da média de mercado.";
    else interpretation = "Engajamento baixo. Necessário investigar barreiras de entrada ou valor percebido.";

    const report = `
Relatório de Análise Automática:

• Taxa de Conversão: ${rateFormatted}%
• Taxa de Retenção Geral: ${rateFormatted}%

Interpretação:
${interpretation}

⚠️ Observação Técnica:
O Tempo Médio de Sessão não pode ser calculado apenas com dados de downloads e usuários ativos. São necessários dados de telemetria de uso real.
    `.trim();

    setAnalysisReport(report);
  };

  useEffect(() => {
    if (app) {
      // Carregar métricas existentes do Firebase
      const loadMetrics = async () => {
        setLoading(true);
        try {
          const metricsDoc = await getDoc(doc(db, 'appMetrics', app.id));
          if (metricsDoc.exists()) {
            setMetrics(metricsDoc.data());
          } else {
            // Inicializar com valores padrão se não existir
            setMetrics({
              activeUsers: 0,
              totalDownloads: 0,
              retentionRate: 0,
              avgSessionTime: 0,
              conversionRate: 0
            });
          }
        } catch (error) {
          console.error('Erro ao carregar métricas:', error);
        } finally {
          setLoading(false);
        }
      };

      loadMetrics();
    }
  }, [app]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMetrics(prev => ({
      ...prev,
      [name]: Number(value) // Converter para número
    }));
  };

  const handleSaveMetrics = async () => {
    if (!app) return;

    setLoading(true);
    try {
      // Salvar no Firebase
      await setDoc(doc(db, 'appMetrics', app.id), metrics);

      if (onUpdateMetrics) {
        onUpdateMetrics(app.id, metrics);
      }

      alert('Métricas salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar métricas:', error);
      alert('Erro ao salvar métricas. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const textColorSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-500';
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';
  const footerBg = isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200';

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      ></div>

      {/* Modal lateral */}
      <div className={`fixed right-0 top-0 h-full w-full max-w-md shadow-xl z-50 transform transition-transform duration-300 ease-in-out translate-x-0 ${modalBg}`}>
        <div className="flex flex-col h-full">
          {/* Cabeçalho */}
          <div className={`flex items-center justify-between p-6 border-b ${borderColor}`}>
            <h2 className={`text-xl font-semibold ${textColor}`}>Gerenciar Aplicativo</h2>
            <button
              onClick={onClose}
              className={`${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Conteúdo */}
          <div className="flex-1 overflow-y-auto p-6">
            {app && (
              <>
                <div className="mb-6">
                  <h3 className={`text-lg font-medium ${textColor} mb-2`}>{app.name}</h3>
                  {/* 🔥 SUBSTITUÍDO: URL pelo ID */}
                  <div className={`text-sm ${textColorSecondary}`}>ID: {app.id}</div>
                  <p className={`text-sm ${textColorSecondary} mt-1`}>Proprietário: {app.userEmail}</p>
                </div>

                <div className="space-y-6">
                  <h4 className={`font-medium ${textColor}`}>Métricas do Aplicativo</h4>

                  {loading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${textColorSecondary}`}>
                          Usuários Ativos
                        </label>
                        <input
                          type="number"
                          name="activeUsers"
                          value={metrics.activeUsers}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBg}`}
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${textColorSecondary}`}>
                          Downloads Totais
                        </label>
                        <input
                          type="number"
                          name="totalDownloads"
                          value={metrics.totalDownloads}
                          onChange={handleInputChange}
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBg}`}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleAutoAnalysis}
                        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Gerar Análise IA
                      </button>

                      {analysisReport && (
                        <div className={`p-4 rounded-lg text-sm whitespace-pre-line ${isDarkMode ? 'bg-indigo-900/30 text-indigo-200 border border-indigo-700' : 'bg-indigo-50 text-indigo-800 border border-indigo-200'}`}>
                          {analysisReport}
                        </div>
                      )}

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${textColorSecondary}`}>
                          Taxa de Retenção (%)
                        </label>
                        <input
                          type="number"
                          name="retentionRate"
                          value={metrics.retentionRate}
                          onChange={handleInputChange}
                          min="0"
                          max="100"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBg}`}
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${textColorSecondary}`}>
                          Tempo Médio de Sessão (minutos)
                        </label>
                        <input
                          type="number"
                          name="avgSessionTime"
                          value={metrics.avgSessionTime}
                          onChange={handleInputChange}
                          step="0.1"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBg}`}
                        />
                      </div>

                      <div>
                        <label className={`block text-sm font-medium mb-1 ${textColorSecondary}`}>
                          Taxa de Conversão (%)
                        </label>
                        <input
                          type="number"
                          name="conversionRate"
                          value={metrics.conversionRate}
                          onChange={handleInputChange}
                          min="0"
                          max="100"
                          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${inputBg}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Rodapé com ações */}
          <div className={`p-6 border-t space-y-3 ${footerBg} ${borderColor}`}>
            <button
              onClick={handleSaveMetrics}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : 'Salvar Métricas'}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  onAlert(user);
                  onClose();
                }}
                className="bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2"
              >
                Enviar Alerta
              </button>

              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Deletar
              </button>

              <ConfirmModal
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={() => {
                  setShowDeleteConfirm(false);
                  onDelete(app.id);
                  onClose();
                }}
                title="Deletar aplicativo"
                message={`Tem certeza que deseja deletar o app "${app?.name}"? Esta ação não pode ser desfeita.`}
                confirmText="Deletar"
                cancelText="Cancelar"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const AppsTable = ({
  allUsers,
  updateAppStatus,
  setSelectedApp,
  setSelectedUser,
  setAlertModalOpen,
  filteredApps,
  deleteApp,
  updateAppMetrics,
  currentUser
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [managementModalOpen, setManagementModalOpen] = useState(false);
  const [selectedAppForManagement, setSelectedAppForManagement] = useState(null);
  const { isDarkMode } = useTheme();

  // 🔥 ESTADOS DE PAGINAÇÃO INTERNA
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(4); // 4 itens por página

  // 🔥 NOVOS ESTADOS PARA FILTRO
  const [filterType, setFilterType] = useState('all'); // 'all', 'new', 'verified'
  const [sortOrder, setSortOrder] = useState('newest'); // 'newest', 'oldest'
  const [appStatusFilter, setAppStatusFilter] = useState(''); // 🔥 NOVO ESTADO: Status do App

  // 🔥 ESTADOS PARA UI MINIMALISTA
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [localSearchTerm, setLocalSearchTerm] = useState('');

  // 🔥 FUNÇÃO ATUALIZADA PARA FILTRAR E ORDENAR OS APPS
  const getFilteredAndSortedApps = () => {
    let filtered = filteredApps || [];

    // Aplicar filtro por tipo de usuário
    if (filterType === 'new') {
      filtered = filtered.filter(app => {
        const user = allUsers.find(u => u.email === app.userEmail);
        return user?.isNewUser && user?.userStatus === 'pending';
      });
    } else if (filterType === 'verified') {
      filtered = filtered.filter(app => {
        const user = allUsers.find(u => u.email === app.userEmail);
        return user?.userStatus === 'verified';
      });
    }

    // 🔥 APLICAR FILTRO POR STATUS DO APP
    if (appStatusFilter) {
      filtered = filtered.filter(app => app.status === appStatusFilter);
    }

    // Aplicar termo de busca local
    if (localSearchTerm.trim() !== '') {
      const term = localSearchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        (app.name && app.name.toLowerCase().includes(term)) ||
        (app.userEmail && app.userEmail.toLowerCase().includes(term)) ||
        (app.id && app.id.toLowerCase().includes(term))
      );
    }

    // Aplicar ordenação por data
    filtered.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);

      return sortOrder === 'newest'
        ? dateB - dateA
        : dateA - dateB;
    });

    return filtered;
  };

  // 🔥 APPS FILTRADOS E ORDENADOS
  const processedApps = getFilteredAndSortedApps();

  // 🔥 CÁLCULOS DE PAGINAÇÃO
  const safeFilteredApps = processedApps;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedItems = safeFilteredApps.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(safeFilteredApps.length / itemsPerPage);

  // 🔥 FUNÇÃO PAGINATE
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // 🔥 RESETAR PAGINAÇÃO QUANDO MUDAR O FILTRO
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType, sortOrder, appStatusFilter]);

  const statusStyles = {
    pending: isDarkMode ? 'text-yellow-300 border-yellow-700' : 'text-yellow-800 border-yellow-200',
    development: isDarkMode ? 'text-blue-300 border-blue-700' : 'text-blue-800 border-blue-200',
    review: isDarkMode ? 'text-purple-300 border-purple-700' : 'text-purple-800 border-purple-200',
    published: isDarkMode ? 'text-green-300 border-green-700' : 'text-green-800 border-green-200',
    active: isDarkMode ? 'text-teal-300 border-teal-700' : 'text-teal-800 border-teal-200',
    rejected: isDarkMode ? 'text-red-300 border-red-700' : 'text-red-800 border-red-200'
  };

  // Classes condicionais para dark mode
  const borderColor = isDarkMode ? 'border-gray-700' : 'border-gray-100';
  const textColor = isDarkMode ? 'text-gray-100' : 'text-gray-800';
  const textColorSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-600';
  const cardBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50';
  const dropdownBg = isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200';
  const dropdownHover = isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100';
  const dropdownText = isDarkMode ? 'text-gray-200' : 'text-gray-700';

  // 🔥 FUNÇÃO ATUALIZADA - Marcar usuário como "não novo" quando status mudar
  const handleStatusChange = async (appId, newStatus) => {
    // Primeiro atualiza o status do app
    updateAppStatus(appId, newStatus);

    // Se o status mudar de "pending", marca o usuário como não novo
    if (newStatus !== 'pending') {
      try {
        // Encontrar o usuário dono do app
        const app = paginatedItems.find(item => item.id === appId);
        if (app && app.userEmail) {
          // Encontrar o usuário na lista de users
          const user = allUsers.find(u => u.email === app.userEmail);
          if (user && user.uid) {
            // Atualizar no Firestore
            await updateDoc(doc(db, 'users', user.uid), {
              isNewUser: false,
              userStatus: newStatus,
              updatedAt: new Date()
            });
            console.log(`✅ Usuário ${user.email} marcado como não novo`);
          }
        }
      } catch (error) {
        console.error('Erro ao atualizar status do usuário:', error);
      }
    }
  };

  const toggleDropdown = (appId) => {
    setActiveDropdown(activeDropdown === appId ? null : appId);
  };

  const openManagementModal = (app) => {
    setSelectedAppForManagement(app);
    setManagementModalOpen(true);
    setActiveDropdown(null);
  };

  const handleUpdateMetrics = (appId, metrics) => {
    if (typeof updateAppMetrics === 'function') {
      updateAppMetrics(appId, metrics);
    }
  };

  // 🔥 FUNÇÃO PARA RENDERIZAR A LOGO DO APP
  const renderAppLogo = (app) => {
    // Se o app tiver uma logo, exibe a imagem
    if (app.logo) {
      return (
        <img 
          src={app.logo} 
          alt={`Logo ${app.name}`}
          className="w-10 h-10 rounded-lg object-cover"
          onError={(e) => {
            // Se a imagem não carregar, exibe fallback
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
      );
    }
    
    // Fallback: ícone do app com a primeira letra do nome
    const firstLetter = app.name ? app.name.charAt(0).toUpperCase() : 'A';
    const colorClass = isDarkMode ? 'bg-blue-900/50 text-blue-300' : 'bg-blue-100 text-blue-600';
    
    return (
      <div className={`w-10 h-10 flex items-center justify-center rounded-lg ${colorClass} font-semibold text-lg`}>
        {firstLetter}
      </div>
    );
  };

  return (
    <div>
      <AppManagementModal
        isOpen={managementModalOpen}
        onClose={() => setManagementModalOpen(false)}
        app={selectedAppForManagement}
        user={selectedAppForManagement ? allUsers.find(u => u.email === selectedAppForManagement.userEmail) : null}
        onDelete={deleteApp}
        onAlert={(user) => {
          setSelectedUser(user);
          setAlertModalOpen(true);
        }}
        onUpdateMetrics={handleUpdateMetrics}
      />

      <div>
        {/* 🔥 HEADER REDESENHADO - CLEAN & MODERN */}
        <div className="mb-2">
          <div className="flex items-center justify-end gap-3">
            
            {/* Lupa / Busca Retrátil */}
            <div className={`flex items-center overflow-hidden transition-all duration-300 ease-out border ${isSearchOpen ? 'w-[200px] sm:w-64 opacity-100' : 'w-10 opacity-70 hover:opacity-100'} ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
              <button 
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`p-2.5 flex-shrink-0 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                title="Buscar aplicativo"
              >
                <FiSearch size={16} />
              </button>
              
              <input
                type="text"
                placeholder="Buscar por nome, email ou ID..."
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                className={`w-full bg-transparent text-sm focus:outline-none placeholder-gray-500 ${isSearchOpen ? 'px-2' : 'px-0'} ${isDarkMode ? 'text-white' : 'text-black'}`}
              />
              
              {isSearchOpen && localSearchTerm && (
                <button 
                  onClick={() => setLocalSearchTerm('')}
                  className={`p-2.5 flex-shrink-0 ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
                >
                  <FiX size={14} />
                </button>
              )}
            </div>

            {/* Menu Dropdown de Filtros */}
            <div className="relative">
              <button
                onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                className={`p-2.5 border transition-all ${isFiltersOpen || filterType !== 'all' || appStatusFilter !== '' || sortOrder !== 'newest' ? (isDarkMode ? 'border-white text-black bg-white' : 'border-black text-white bg-black') : (isDarkMode ? 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 bg-gray-800' : 'border-gray-200 text-gray-500 hover:text-black hover:border-gray-400 bg-gray-50')}`}
                title="Filtrar"
              >
                <FiFilter size={16} />
              </button>

              {/* Submenu de Filtros */}
              {isFiltersOpen && (
                <div className={`absolute right-0 mt-2 w-[280px] sm:w-64 border shadow-2xl z-20 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className={`p-3 border-b flex justify-between items-center ${isDarkMode ? 'border-gray-800 bg-black' : 'border-gray-100 bg-gray-50'}`}>
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Filtros</span>
                    <button onClick={() => setIsFiltersOpen(false)} className={`${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}>
                      <FiX size={14} />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-4">
                    {/* Status Usuários */}
                    <div className="space-y-2">
                      <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Usuários</label>
                      <select
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      >
                        <option value="all">Todos os usuários</option>
                        <option value="new">Apenas novos</option>
                        <option value="verified">Verificados</option>
                      </select>
                    </div>
                    
                    {/* Status App */}
                    <div className="space-y-2">
                      <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Status do App</label>
                      <select
                        value={appStatusFilter}
                        onChange={(e) => setAppStatusFilter(e.target.value)}
                        className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      >
                        <option value="">Todos os status</option>
                        <option value="pending">Pendente</option>
                        <option value="published">Publicado</option>
                        <option value="development">Em Desenv.</option>
                        <option value="active">Ativo</option>
                        <option value="rejected">Rejeitado</option>
                      </select>
                    </div>

                    {/* Ordenação */}
                    <div className="space-y-2">
                      <label className={`block text-[10px] font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Ordenação</label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className={`w-full appearance-none px-3 py-2 text-sm font-semibold border focus:outline-none ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}
                      >
                        <option value="newest">Mais recentes primeiro</option>
                        <option value="oldest">Mais antigos primeiro</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Fim Header Minimalista */}
        </div>

        {/* Container da Tabela (Versões Desktop e Mobile) */}
        <div className={`border rounded-xl overflow-hidden ${isDarkMode ? 'border-gray-800 bg-gray-900/30' : 'border-gray-200 bg-white'}`}>
          {/* Versão Desktop */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left border-b ${isDarkMode ? 'bg-gray-900/80 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Aplicativo
                  </th>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Proprietário
                  </th>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Valor
                  </th>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Data
                  </th>
                  <th className={`px-6 py-4 text-[10px] sm:text-xs font-bold uppercase tracking-widest text-right ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDarkMode ? 'divide-gray-800' : 'divide-gray-200'}`}>
                {paginatedItems.map((app) => (
                  <tr key={app.id} className={`transition-all duration-300 group ${isDarkMode ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'} hover:shadow-[4px_4px_0px_0px_rgba(99,102,241,0.05)]`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {/* 🔥 SUBSTITUÍDO: Ícone do celular pela logo do app */}
                        {renderAppLogo(app)}
                        <div>
                          <div className={`text-sm font-semibold truncate max-w-[150px] ${textColor}`}>
                            {app.name}
                          </div>
                          {/* 🔥 SUBSTITUÍDO: URL pelo ID */}
                          <div className={`text-xs mt-1 truncate max-w-[150px] ${textColorSecondary}`}>
                            ID: {app.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className={`text-sm truncate max-w-[120px] ${textColor}`}>
                          {app.userEmail}
                        </div>
                        {/* 🔥 BADGE DO USUÁRIO NOVO */}
                        <NewUserBadge
                          userId={allUsers.find(u => u.email === app.userEmail)?.uid}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="relative">
                        <select
                          value={app.status || 'pending'}
                          onChange={(e) => handleStatusChange(app.id, e.target.value)}
                          className={`w-full text-xs font-bold tracking-widest uppercase py-2.5 pl-3 pr-8 border focus:outline-none focus:ring-0 cursor-pointer transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} ${statusStyles[app.status || 'pending']
                            }`}
                          style={{ appearance: 'none' }}
                        >
                          <option value="pending">Pendente</option>
                          <option value="development">Em Desenvolvimento</option>
                          <option value="review">Em Revisão</option>
                          <option value="published">Publicado</option>
                          <option value="active">Ativo</option>
                          <option value="rejected">Rejeitado</option>
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                        }`}>
                        R$ {app.totalPrice?.toFixed(2) || '0,00'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm px-3 py-1 rounded-md ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-50 text-gray-500'
                        }`}>
                        {app.createdAt?.toLocaleDateString('pt-BR')}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => setSelectedApp(app)}
                          className={`p-2 rounded-lg transition-colors ${isDarkMode
                              ? 'text-blue-400 hover:text-blue-300 hover:bg-blue-900/50'
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                            }`}
                          title="Ver detalhes"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        </button>

                        <div className="relative">
                          <button
                            className={`p-2 rounded-lg transition-colors ${isDarkMode
                                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                              }`}
                            title="Mais ações"
                            onClick={() => toggleDropdown(app.id)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"></path>
                            </svg>
                          </button>

                          {activeDropdown === app.id && (
                            <div className={`absolute right-0 mt-1 w-48 rounded-lg shadow-lg border py-1 z-10 ${dropdownBg}`}>
                              <button
                                onClick={() => openManagementModal(app)}
                                className={`flex items-center w-full px-4 py-2 text-sm transition-colors ${dropdownText} ${dropdownHover}`}
                              >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Gerenciar App
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Versão Mobile - Totalmente Responsiva */}
          <div className="lg:hidden space-y-3 p-3">
            {paginatedItems.map((app) => (
              <div key={app.id} className={`rounded-xl p-3 space-y-3 ${cardBg} shadow-sm`}>
                {/* Cabeçalho com App Info e Badge */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    {/* 🔥 SUBSTITUÍDO: Ícone do celular pela logo do app (Mobile) */}
                    {renderAppLogo(app)}
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-semibold truncate ${textColor}`}>
                        {app.name}
                      </div>
                      {/* 🔥 SUBSTITUÍDO: URL pelo ID */}
                      <div className={`text-xs truncate ${textColorSecondary} mt-0.5`}>
                        ID: {app.id}
                      </div>
                    </div>
                  </div>
                  {/* 🔥 BADGE MOBILE */}
                  <div className="flex-shrink-0">
                    <NewUserBadge
                      userId={allUsers.find(u => u.email === app.userEmail)?.uid}
                    />
                  </div>
                </div>

                {/* Grid de Informações - Layout Responsivo */}
                <div className="grid grid-cols-1 xs:grid-cols-2 gap-2 text-sm">
                  {/* Proprietário */}
                  <div className="xs:col-span-1 min-w-0">
                    <div className={`text-xs mb-1 ${textColorSecondary}`}>Proprietário</div>
                    <div className={`truncate text-sm ${textColor}`}>{app.userEmail}</div>
                  </div>

                  {/* Valor */}
                  <div className="xs:col-span-1">
                    <div className={`text-xs mb-1 ${textColorSecondary}`}>Valor</div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-green-100 text-green-800'
                      }`}>
                      R$ {app.totalPrice?.toFixed(2) || '0,00'}
                    </span>
                  </div>

                  {/* Status - Ocupa linha inteira */}
                  <div className="col-span-2">
                    <div className={`text-xs mb-1 ${textColorSecondary}`}>Status</div>
                    <select
                      value={app.status || 'pending'}
                      onChange={(e) => handleStatusChange(app.id, e.target.value)}
                      className={`w-full text-sm rounded-lg py-2 px-3 border-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer ${isDarkMode ? 'bg-gray-700' : 'bg-white'} ${statusStyles[app.status || 'pending']
                        }`}
                      style={{ appearance: 'none' }}
                    >
                      <option value="pending">Pendente</option>
                      <option value="development">Em Desenvolvimento</option>
                      <option value="review">Em Revisão</option>
                      <option value="published">Publicado</option>
                      <option value="active">Ativo</option>
                      <option value="rejected">Rejeitado</option>
                    </select>
                  </div>

                  {/* Data - Ocupa linha inteira */}
                  <div className="col-span-2">
                    <div className={`text-xs mb-1 ${textColorSecondary}`}>Data de Criação</div>
                    <div className={`text-sm ${textColor}`}>
                      {app.createdAt?.toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                </div>

                {/* Botões de Ação - Layout Responsivo */}
                <div className="flex flex-col xs:flex-row gap-2 pt-2">
                  <button
                    onClick={() => setSelectedApp(app)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                        ? 'bg-blue-900/50 text-blue-300 hover:bg-blue-800/50'
                        : 'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}
                  >
                    Ver Detalhes
                  </button>
                  <button
                    onClick={() => openManagementModal(app)}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${isDarkMode
                        ? 'bg-gray-600 text-gray-200 hover:bg-gray-500'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                  >
                    Gerenciar
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className={`px-6 py-4 border-t ${borderColor}`}>
            <Pagination
              filteredItems={safeFilteredApps}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              indexOfFirstItem={indexOfFirstItem}
              indexOfLastItem={indexOfLastItem}
              totalPages={totalPages}
              paginate={paginate}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppsTable;